import type { GamificationPeriod } from '@troia-v3/types';

const MONTHS = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

/** Detecta o período a partir do formato da chave: `YYYY-Www` | `YYYY-Qn` | `YYYY-MM`. */
export function detectPeriod(periodKey: string): GamificationPeriod {
  if (/^\d{4}-W\d{2}$/.test(periodKey)) return 'week';
  if (/^\d{4}-Q[1-4]$/.test(periodKey)) return 'quarter';
  return 'month';
}

/** "Agosto 2026" · "Semana 34 · 2026" · "3º trimestre 2026" (mesmo texto do `.per-chip` do mockup). */
export function formatPeriodLabel(period: GamificationPeriod, periodKey: string): string {
  const [year = '', rest = ''] = periodKey.split('-');
  if (period === 'week') return `Semana ${Number(rest.slice(1))} · ${year}`;
  if (period === 'quarter') return `${rest.slice(1)}º trimestre ${year}`;
  return `${MONTHS[Number(rest) - 1] ?? rest} ${year}`;
}

/** Intervalo personalizado (`yyyy-mm-dd`) → "01/09 – 15/09/2026" (ano só no fim quando é o mesmo). */
export function formatRangeLabel(startDate: string, endDate: string): string {
  const day = (ymd: string): string => `${ymd.slice(8, 10)}/${ymd.slice(5, 7)}`;
  const full = (ymd: string): string => `${day(ymd)}/${ymd.slice(0, 4)}`;
  return startDate.slice(0, 4) === endDate.slice(0, 4)
    ? `${day(startDate)} – ${full(endDate)}`
    : `${full(startDate)} – ${full(endDate)}`;
}

/** "no mês" · "na semana" · "no trimestre" — complemento de rótulos ("Confirmados {…}"). */
export function periodInLabel(period: GamificationPeriod): string {
  if (period === 'week') return 'na semana';
  if (period === 'quarter') return 'no trimestre';
  return 'no mês';
}

/** "neste mês" · "nesta semana" · "neste trimestre" — para frases sobre o período corrente. */
export function periodThisLabel(period: GamificationPeriod): string {
  if (period === 'week') return 'nesta semana';
  if (period === 'quarter') return 'neste trimestre';
  return 'neste mês';
}

/** Semana ISO (segunda-feira) — mesmo algoritmo do backend (luxon) sem dependência. */
function isoWeek(date: Date): { weekYear: number; week: number } {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return { weekYear: d.getUTCFullYear(), week: Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7) };
}

/** Chave do período atual — `YYYY-MM` | `YYYY-Www` (ISO) | `YYYY-Qn`. */
export function currentPeriodKey(period: GamificationPeriod, date: Date = new Date()): string {
  if (period === 'week') {
    const { weekYear, week } = isoWeek(date);
    return `${weekYear}-W${String(week).padStart(2, '0')}`;
  }
  if (period === 'quarter') return `${date.getFullYear()}-Q${Math.floor(date.getMonth() / 3) + 1}`;
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

function shift(periodKey: string, delta: number): string {
  const period = detectPeriod(periodKey);
  const [y = '0', rest = ''] = periodKey.split('-');
  const year = Number(y);
  if (period === 'month') {
    const idx = year * 12 + (Number(rest) - 1) + delta;
    return `${Math.floor(idx / 12)}-${String((idx % 12) + 1).padStart(2, '0')}`;
  }
  if (period === 'quarter') {
    const idx = year * 4 + (Number(rest.slice(1)) - 1) + delta;
    return `${Math.floor(idx / 4)}-Q${(idx % 4) + 1}`;
  }
  // week: volta pela data da segunda-feira da semana ISO
  const week = Number(rest.slice(1));
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const jan4Day = jan4.getUTCDay() || 7;
  const monday = new Date(jan4.getTime() - (jan4Day - 1) * 86400000 + (week - 1) * 7 * 86400000 + delta * 7 * 86400000);
  const { weekYear, week: w } = isoWeek(new Date(monday.getUTCFullYear(), monday.getUTCMonth(), monday.getUTCDate()));
  return `${weekYear}-W${String(w).padStart(2, '0')}`;
}

/** `n` chaves anteriores, da mais recente para a mais antiga. */
export function previousPeriodKeys(periodKey: string, n: number): string[] {
  const keys: string[] = [];
  let cursor = periodKey;
  for (let i = 0; i < n; i += 1) {
    cursor = shift(cursor, -1);
    keys.push(cursor);
  }
  return keys;
}
